/**
 * Bloks Feature Highlights — accordion rows with click / scroll activation.
 * Opens from scroll position through the stack or click/focus — not hover.
 *
 * Note: Horizon scrolls `.page-wrapper` on desktop (html/body overflow hidden),
 * so we listen there as well as on window, and use IntersectionObserver as a backup.
 */
class BloksFeatureHighlights extends HTMLElement {
  connectedCallback() {
    this.rows = [...this.querySelectorAll('[data-feature-row]')];
    this.triggers = [...this.querySelectorAll('[data-feature-trigger]')];
    this.stack = this.querySelector('[data-feature-stack]');

    if (!this.rows.length || !this.triggers.length) return;

    this.activeIndex = Math.max(
      0,
      this.rows.findIndex((row) => row.classList.contains('is-active'))
    );
    this.scrollActivate = this.dataset.scrollActivate !== 'false';
    this.rafId = null;
    this.observer = null;
    this.scrollTargets = [];

    this.onTriggerFocus = this.onTriggerFocus.bind(this);
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onScrollCheck = this.onScrollCheck.bind(this);

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('focus', this.onTriggerFocus);
      trigger.addEventListener('click', this.onTriggerClick);
    });

    this.activate(this.activeIndex);

    if (this.scrollActivate) {
      this.setupScrollActivate();
    }
  }

  disconnectedCallback() {
    this.triggers?.forEach((trigger) => {
      trigger.removeEventListener('focus', this.onTriggerFocus);
      trigger.removeEventListener('click', this.onTriggerClick);
    });

    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    this.observer?.disconnect();
    this.observer = null;

    this.scrollTargets.forEach((target) => {
      target.removeEventListener('scroll', this.onScrollCheck);
    });
    this.scrollTargets = [];
    window.removeEventListener('resize', this.onScrollCheck);
  }

  getScrollTargets() {
    const targets = [window];
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper) targets.push(wrapper);
    return targets;
  }

  setupScrollActivate() {
    this.scrollTargets = this.getScrollTargets();
    this.scrollTargets.forEach((target) => {
      target.addEventListener('scroll', this.onScrollCheck, { passive: true });
    });
    window.addEventListener('resize', this.onScrollCheck, { passive: true });

    // Fires when the stack moves through the viewport even if scroll isn't on window.
    if ('IntersectionObserver' in window && this.stack) {
      this.observer = new IntersectionObserver(() => this.onScrollCheck(), {
        root: null,
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
      });
      this.observer.observe(this.stack);
    }

    this.onScrollCheck();
  }

  onTriggerFocus(event) {
    const index = Number(event.currentTarget.dataset.index);
    if (Number.isNaN(index)) return;
    this.activate(index);
  }

  onTriggerClick(event) {
    const index = Number(event.currentTarget.dataset.index);
    if (Number.isNaN(index)) return;
    this.activate(index);
  }

  onScrollCheck() {
    if (!this.scrollActivate) return;
    if (this.rafId) return;

    this.rafId = window.requestAnimationFrame(() => {
      this.rafId = null;
      this.syncFromScroll();
    });
  }

  /**
   * Which equal band of the stack the viewport line sits in.
   * Uses the stack box (stable) — not per-row centers that jump when flex grows.
   */
  syncFromScroll() {
    if (!this.rows.length || !this.stack) return;

    const rect = this.stack.getBoundingClientRect();
    if (rect.height <= 0) return;

    // Slightly above center so the next feature opens a bit earlier.
    const line = window.innerHeight * 0.45;
    const n = this.rows.length;
    const progress = (line - rect.top) / rect.height;
    const clamped = Math.max(0, Math.min(0.9999, progress));
    const ideal = Math.min(n - 1, Math.floor(clamped * n));

    if (ideal === this.activeIndex) return;

    // Light dead zone only for adjacent flips (flex-transition flicker).
    if (Math.abs(ideal - this.activeIndex) === 1) {
      const pad = 0.08 / n;
      if (ideal > this.activeIndex && clamped < (this.activeIndex + 1) / n + pad) return;
      if (ideal < this.activeIndex && clamped > this.activeIndex / n - pad) return;
    }

    this.activate(ideal);
  }

  activate(index) {
    if (index < 0 || index >= this.rows.length) return;
    if (index === this.activeIndex && this.rows[index]?.classList.contains('is-active')) {
      this.syncVideo(this.rows[index], true);
      return;
    }

    this.activeIndex = index;

    this.rows.forEach((row, i) => {
      const isActive = i === index;
      row.classList.toggle('is-active', isActive);
      this.syncVideo(row, isActive);
    });

    this.triggers.forEach((trigger, i) => {
      const isActive = i === index;
      trigger.setAttribute('aria-pressed', String(isActive));
      trigger.setAttribute('aria-expanded', String(isActive));
      if (isActive) {
        trigger.setAttribute('aria-current', 'true');
      } else {
        trigger.removeAttribute('aria-current');
      }
    });
  }

  syncVideo(row, shouldPlay) {
    const video = row.querySelector('video');
    if (!video) return;

    if (shouldPlay) {
      video.muted = true;
      const playPromise = video.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    } else {
      video.pause();
      try {
        video.currentTime = 0;
      } catch {
        /* ignore seek errors on unloaded sources */
      }
    }
  }
}

if (!customElements.get('bloks-feature-highlights')) {
  customElements.define('bloks-feature-highlights', BloksFeatureHighlights);
}
